// @ts-nocheck
import Editor from './../../../../components/Editor/Editor';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import './DayHeader.css';

const DayHeader = ({
  dayNumber,
  title,
  description,
  backgroundImage,
  badgeColor = '#ffffff',
  textColor = '#2c3e50'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageHeight, setImageHeight] = useState(0);
  const [needsShowMore, setNeedsShowMore] = useState(false);
  const imageRef = useRef(null);

  // Determine which variant to use
  const hasImage = !!backgroundImage;
  const variant = hasImage ? 'with-image' : 'no-image';

  // Measure image height for description matching
  useEffect(() => {
    if (hasImage && imageRef.current) {
      const updateImageHeight = () => {
        const height = imageRef.current?.offsetHeight || 0;
        setImageHeight(height - 42); // Subtracting 42px for padding/margin adjustments
      };

      // Initial measurement
      updateImageHeight();

      // Listen for window resize
      window.addEventListener('resize', updateImageHeight);

      // Listen for image load
      const img = imageRef.current?.querySelector('img');
      if (img) {
        img.addEventListener('load', updateImageHeight);
      }

      return () => {
        window.removeEventListener('resize', updateImageHeight);
        if (img) {
          img.removeEventListener('load', updateImageHeight);
        }
      };
    }
  }, [hasImage, backgroundImage]);

  // Check if description needs "Show More" functionality
  useEffect(() => {
    if (description && description.length > 200) {
      setNeedsShowMore(true);
    } else {
      setNeedsShowMore(false);
    }
  }, [description]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`day-header day-header--${variant}`}>
      {/* Variant 1 & 3: With Image */}
      {hasImage && (
        <>
          {/* Background Image */}
          <div className='day-header__image' ref={imageRef}>
            <img
              src={backgroundImage}
              alt={title}
              className='day-header__bg-image'
            />
            <div className='day-header__overlay'></div>

            {/* Mobile Badge Overlay (Variant 1) */}
            <div
              className='day-header__mobile-badge'
              style={{
                backgroundColor: badgeColor,
                color: '#1d2530',
                fontWeight: '600'
              }}
            >
              Day {dayNumber}
            </div>

            {/* Mobile Title Overlay (Variant 1) */}
            <div className='day-header__mobile-content'>
              <h2 className='day-header__mobile-title'>{title}</h2>
            </div>
          </div>

          {/* Desktop Content (Variant 3) */}
          <div className='day-header__desktop-content'>
            <div className='day-header__desktop-header'>
              <div
                className='day-header__badge'
                style={{ backgroundColor: '#e8eaee', color: '#627084' }}
              >
                Day {dayNumber}
              </div>

              <h2 className='day-header__title' style={{ color: textColor }}>
                {title}
              </h2>
            </div>

            {/* Desktop Description - matches image height */}
            {description && (
              <div className='day-header__desktop-description'>
                <div
                  className={`day-header__description-container ${
                    !isExpanded
                      ? 'day-header__description-container--collapsed'
                      : ''
                  }`}
                  style={{
                    maxHeight: !isExpanded ? `${imageHeight}px` : 'none'
                  }}
                >
                  <Editor
                    editable={false}
                    isPreviewing={true}
                    content={description}
                    id={`day-description-editor-desktop-${dayNumber}`}
                    setDescription={() => {}}
                    setMarkdown={() => {}}
                    showActions={false}
                    placeholder={``}
                    generatePrompt={null}
                    setIsPreviewing={() => {}}
                  />

                  {/* Gradient overlay when collapsed */}
                  {!isExpanded && needsShowMore && (
                    <div className='day-header__description-gradient' />
                  )}
                </div>

                {/* Show More/Less Link */}
                {needsShowMore && (
                  <div className='day-header__show-more-container'>
                    <span
                      className='day-header__show-more-link'
                      onClick={toggleExpanded}
                      role='button'
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-controls={`day-description-editor-desktop-${dayNumber}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded();
                        }
                      }}
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Description Below Image (Variant 1) */}
          {description && (
            <div className='day-header__mobile-description'>
              <div
                className={`day-header__description-container ${
                  !isExpanded
                    ? 'day-header__description-container--collapsed-mobile'
                    : ''
                }`}
              >
                <Editor
                  editable={false}
                  isPreviewing={true}
                  content={description}
                  id={`day-description-editor-mobile-${dayNumber}`}
                  setDescription={() => {}}
                  setMarkdown={() => {}}
                  setIsPreviewing={() => {}}
                  showActions={false}
                  placeholder={``}
                  generatePrompt={null}
                />

                {/* Gradient overlay when collapsed */}
                {!isExpanded && needsShowMore && (
                  <div className='day-header__description-gradient' />
                )}
              </div>

              {/* Show More/Less Link */}
              {needsShowMore && (
                <div className='day-header__show-more-container'>
                  <span
                    className='day-header__show-more-link'
                    onClick={toggleExpanded}
                    role='button'
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    aria-controls={`day-description-editor-mobile-${dayNumber}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded();
                      }
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Variant 2: No Image */}
      {!hasImage && (
        <div className='day-header__no-image-content'>
          <div
            className='day-header__badge'
            style={{ backgroundColor: '#e8eaee', color: '#627084' }}
          >
            Day {dayNumber}
          </div>

          <h2 className='day-header__title' style={{ color: textColor }}>
            {title}
          </h2>

          {description && (
            <div className='day-header__no-image-description'>
              <div
                className={`day-header__description-container ${
                  !isExpanded
                    ? 'day-header__description-container--collapsed-no-image'
                    : ''
                }`}
              >
                <Editor
                  editable={false}
                  isPreviewing={true}
                  content={description}
                  id={`day-description-editor-no-image-${dayNumber}`}
                  setDescription={() => {}}
                  setMarkdown={() => {}}
                  setIsPreviewing={() => {}}
                  showActions={false}
                  placeholder={``}
                  generatePrompt={null}
                />

                {/* Gradient overlay when collapsed */}
                {!isExpanded && needsShowMore && (
                  <div className='day-header__description-gradient' />
                )}
              </div>

              {/* Show More/Less Link */}
              {needsShowMore && (
                <div className='day-header__show-more-container'>
                  <span
                    className='day-header__show-more-link'
                    onClick={toggleExpanded}
                    role='button'
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    aria-controls={`day-description-editor-no-image-${dayNumber}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded();
                      }
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

DayHeader.propTypes = {
  dayNumber: PropTypes.number.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  backgroundImage: PropTypes.string,
  badgeColor: PropTypes.string,
  textColor: PropTypes.string
};

export default DayHeader;
